/** One window for creating AND editing a report template (the app's centred
 *  modal shell, ConnectorDialog, wide, two columns): what the report covers on
 *  the inline-start side, when it runs on the other - frequency, days, the hour
 *  in the owner's own time zone, the next three runs WITH the days each covers,
 *  an honest line about timing, and the bell. The footer says which model writes
 *  it and what it costs, a report and a month, before anything is saved. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Input, Select, Switch } from 'antd';
import { BellOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { ConnectorDialog, DialogColumn, DialogColumns } from '@modules/chatbots/components/ConnectorDialog';
import insightsService from '../../services/insightsService';
import type {
  AgentOption, ReportFrequency, ReportSection, ReportTemplate, ReportTemplatePayload, SchedulePreview, TenantNode,
} from '../../types/insights';
import { ScopeChoice, scopeDraftOf, scopeOf, scopeProblem, type ScopeDraft } from '../templates/ScopeChoice';
import { formatEstimate } from '../templates/templateParts';
import { reportError, windowLabel } from './reportParts';
import { ScheduleFields, scheduleFields } from './ScheduleFields';

const { TextArea } = Input;
const SECTIONS: ReportSection[] = [
  'key_numbers', 'what_changed', 'trend', 'organizations', 'agents', 'cost', 'templates', 'feedback', 'next_steps',
];
const PREVIEW_DELAY_MS = 400;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What a report covers - everything the owner can see, chosen organizations, or chosen agents - is item 70's choice. */
interface Draft extends ScopeDraft {
  /** The scope was changed here; until then the stored one is saved as it is. */
  scopeEdited: boolean;
  name: string;
  focus: string;
  language: 'en' | 'he';
  sections: ReportSection[];
  frequency: ReportFrequency;
  weekdays: number[];
  monthDay: number;
  runTime: string;
  notify: boolean;
}

interface Props {
  /** null: a new report template. */
  template: ReportTemplate | null;
  tenantTree: TenantNode[];
  agentOptions: AgentOption[];
  onClose: () => void;
  /** `run`: the person asked to run it right after saving. */
  onSaved: (template: ReportTemplate, run: boolean) => void;
}

function toDraft(template: ReportTemplate | null, language: string,
                 agentOptions: Props['agentOptions']): Draft {
  return {
    name: template?.name || '',
    focus: template?.focus || '',
    language: template?.language || (language === 'he' ? 'he' : 'en'),
    sections: template?.sections.length ? template.sections : SECTIONS,
    ...scopeDraftOf(template?.scope, agentOptions),
    scopeEdited: false,
    frequency: template?.frequency || 'weekly',
    weekdays: template?.weekdays.length ? template.weekdays : [language === 'he' ? 6 : 0],
    monthDay: template?.month_day || 1,
    runTime: template?.run_time || '08:00',
    notify: template ? template.notify : true,
  };
}

function reportScope(d: Draft, stored: ReportTemplate['scope'] | undefined): ReportTemplatePayload['scope'] {
  if (!d.scopeEdited) return stored || {};
  const scope = scopeOf(d);
  // A merged agent option may carry a meta-agent's id: a report reads agents with an id only.
  return scope.chatbot_ids ? { chatbot_ids: scope.chatbot_ids.filter((id) => UUID.test(id)) } : scope;
}

function toPayload(d: Draft, stored: ReportTemplate['scope'] | undefined): ReportTemplatePayload {
  return {
    name: d.name.trim(),
    focus: d.focus.trim(),
    language: d.language,
    sections: d.sections.length === SECTIONS.length ? [] : d.sections,
    scope: reportScope(d, stored),
    ...scheduleFields({ frequency: d.frequency, weekdays: d.weekdays, monthDay: d.monthDay, runTime: d.runTime }),
    notify: d.notify,
  };
}

export function ReportTemplateEditor({ template, tenantTree, agentOptions, onClose, onSaved }: Props) {
  const { t, language } = useLanguage();
  const [draft, setDraft] = useState<Draft>(() => toDraft(template, language, agentOptions));
  const [preview, setPreview] = useState<SchedulePreview | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  // A report reads the agents of the owner's organizations (the ones with an id).
  const reportAgents = useMemo(() => agentOptions.filter((o) => o.value.split(',').every((id) => UUID.test(id))),
    [agentOptions]);
  const initial = useRef(JSON.stringify(toPayload(toDraft(template, language, agentOptions), template?.scope)));
  const nameRef = useRef<HTMLInputElement | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const payload = useMemo(() => toPayload(draft, template?.scope), [draft, template?.scope]);
  const dirty = JSON.stringify(payload) !== initial.current;

  // The next runs and the cost, as the server computes them for this schedule
  // (only when the schedule changes: the weekdays array is new on every edit).
  const weekdaysKey = (payload.weekdays || []).join(',');
  useEffect(() => {
    const id = window.setTimeout(() => {
      insightsService.previewReportSchedule({
        frequency: payload.frequency, weekdays: payload.weekdays, month_day: payload.month_day,
        run_time: payload.run_time, report_template_id: template?.template_id,
      }).then(setPreview).catch(() => setPreview(null));
    }, PREVIEW_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.frequency, weekdaysKey, payload.month_day, payload.run_time, template?.template_id]);

  const save = async (run: boolean) => {
    if (!payload.name) {
      setNameError(true);
      nameRef.current?.focus();
      return;
    }
    const scopeMissing = draft.scopeEdited ? scopeProblem(draft) : null;
    if (scopeMissing) {
      setScopeError(t(scopeMissing));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = template
        ? await insightsService.updateReportTemplate(template.template_id, payload)
        : await insightsService.createReportTemplate(payload);
      onSaved(saved, run);
    } catch (err) {
      setError(reportError(t, err));
    } finally {
      setSaving(false);
    }
  };

  const estimate = preview?.estimate;
  const footerNote = estimate ? (
    <span className="rp-estimate">
      <ClockCircleOutlined aria-hidden="true" />
      {draft.frequency === 'none'
        ? t('insights.reports.editor.costByHand', { report: formatEstimate(t, estimate.per_report_usd),
                                                    model: estimate.model })
        : t('insights.reports.editor.cost', { report: formatEstimate(t, estimate.per_report_usd),
                                              month: formatEstimate(t, preview!.per_month_usd),
                                              model: estimate.model })}
    </span>
  ) : undefined;

  return (
    <ConnectorDialog
      title={template ? t('insights.reports.editor.editTitle') : t('insights.reports.editor.newTitle')}
      size="wide"
      onClose={onClose}
      dirty={dirty}
      note={error || footerNote}
      noteTone={error ? 'error' : 'muted'}
      testId="report-template-editor"
      actions={(
        <>
          <button type="button" className="cx-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="cx-btn" onClick={() => save(true)} disabled={saving}>
            {t('insights.reports.editor.saveAndRun')}
          </button>
          <button type="button" className="cx-btn cx-btn--primary" onClick={() => save(false)} disabled={saving}>
            {t('common.save')}
          </button>
        </>
      )}
    >
      <DialogColumns>
        <DialogColumn title={t('insights.reports.editor.covers')}>
          <div className="rp-form">
            <div>
              <label className="rp-label" htmlFor="rp-name">
                {t('insights.reports.editor.name')}<span className="rp-required" aria-hidden="true"> *</span>
              </label>
              <Input id="rp-name" dir="auto" ref={(el) => { nameRef.current = el?.input || null; }} value={draft.name}
                     maxLength={120} status={nameError ? 'error' : undefined}
                     aria-invalid={nameError} aria-describedby={nameError ? 'rp-name-err' : undefined}
                     placeholder={t('insights.reports.editor.namePlaceholder')}
                     onChange={(e) => { set('name', e.target.value); setNameError(false); }} />
              {nameError && <p id="rp-name-err" className="rp-error" role="alert">{t('insights.reports.editor.nameRequired')}</p>}
            </div>
            <div>
              <label className="rp-label" htmlFor="rp-focus">{t('insights.reports.editor.focus')}</label>
              <TextArea id="rp-focus" dir="auto" value={draft.focus} maxLength={2000} autoSize={{ minRows: 3, maxRows: 7 }}
                        placeholder={t('insights.reports.editor.focusPlaceholder')} aria-describedby="rp-focus-help"
                        onChange={(e) => set('focus', e.target.value)} />
              <p id="rp-focus-help" className="rp-help">{t('insights.reports.editor.focusHelp')}</p>
            </div>
            <fieldset className="rp-fieldset" data-testid="report-scope">
              <legend className="rp-label">{t('insights.reports.editor.scope')}</legend>
              <ScopeChoice
                value={draft} tenantTree={tenantTree} agentOptions={reportAgents} className="rp-choices--three"
                testIdPrefix="report-scope"
                onChange={(next) => { setDraft((d) => ({ ...d, ...next, scopeEdited: true })); setScopeError(null); }}
                allHelp={<p className="rp-help">{t('insights.reports.editor.scopeAllHelp')}</p>}
              />
              {scopeError && <p className="rp-error" role="alert">{scopeError}</p>}
            </fieldset>
            <div className="rp-pair">
              <div>
                <label className="rp-label" htmlFor="rp-language">{t('insights.reports.editor.language')}</label>
                <Select id="rp-language" className="rp-full" value={draft.language}
                        onChange={(v) => set('language', v)}
                        options={[{ value: 'en', label: 'English' }, { value: 'he', label: 'עברית' }]} />
              </div>
            </div>
            <fieldset className="rp-fieldset">
              <legend className="rp-label">{t('insights.reports.editor.sections')}</legend>
              <Checkbox.Group className="rp-checks" value={draft.sections}
                              onChange={(v) => set('sections', v as ReportSection[])}
                              options={SECTIONS.map((s) => ({ value: s, label: t(`insights.reports.section.${s}`) }))} />
              <p className="rp-help">{t('insights.reports.editor.sectionsHelp')}</p>
            </fieldset>
          </div>
        </DialogColumn>

        <DialogColumn title={t('insights.reports.editor.when')} aside>
          <div className="rp-form">
            <ScheduleFields
              idPrefix="rp" value={{ frequency: draft.frequency, weekdays: draft.weekdays, monthDay: draft.monthDay,
                                     runTime: draft.runTime }}
              onChange={(v) => setDraft((d) => ({ ...d, frequency: v.frequency, weekdays: v.weekdays,
                                                   monthDay: v.monthDay, runTime: v.runTime }))}
              zone={preview?.zone} nextRuns={preview?.next_runs || []}
              nextRunsLabel={t('insights.reports.editor.nextRuns')}
              runDetail={(run) => t('insights.reports.editor.coversDays', { days: windowLabel(run.covers, { year: false }) })}
              timingNote={t('insights.reports.editor.timing', { time: draft.runTime })}
              byHandNote={t('insights.reports.editor.byHandHelp')} />

            <label className="rp-estimate" htmlFor="rp-notify">
              <Switch id="rp-notify" checked={draft.notify} onChange={(v) => set('notify', v)} size="small" />
              <BellOutlined aria-hidden="true" /> {t('insights.reports.editor.notify')}
            </label>
          </div>
        </DialogColumn>
      </DialogColumns>
    </ConnectorDialog>
  );
}
